import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { getSelectedLocation } from '../../shared/utils/storage-utils';
import { FinancialYearFilterService } from './financial-year-filter.service';

export interface SeniorCenterApplicationListItem {
  applicationId: string;
  applicantName: string;
  applicantEmailAddress: string;
  livesAlone: string;
  submittedDate: string | null;
  approvedDate?: string | null;
  status: string;
  rejectedComment?: string | null;
  updatedAt?: string | null;
  locationId?: string;
  locationName?: string;
  rfidNumber?: string | null;
  transportationOption?: string | null;
  transportationRequestStatus?: string | null;
  /** When transport was approved/rejected; null while pending. */
  transportationApprovedAt?: string | null;
  /** Approval + 1 year (annual reapply). */
  expiresAt?: string | null;
  /** Negative when already expired. */
  daysUntilExpiry?: number | null;
  /** current | expiring | expired */
  expiryStatus?: string | null;
  previousApplicationId?: string | null;
}

export interface SeniorCenterApplicationDetailItem extends SeniorCenterApplicationListItem {
  rejectedComment?: string | null;
  previousApplicationId?: string | null;
  nextApplicationId?: string | null;
  /** Recipient DOB from application form_json. */
  applicantDateOfBirth?: string | null;
  /** Whole years of age as of today; null when DOB is missing/invalid. */
  applicantAge?: number | null;
}

export interface SeniorCenterApplicationsListResponse {
  statusCode: number;
  message: string;
  programId: number;
  total: number;
  /** Approved apps within 60 days of annual expiry (location-scoped). */
  expiringCount?: number;
  /** Approved apps past annual expiry (location-scoped). */
  expiredCount?: number;
  applications: SeniorCenterApplicationListItem[];
}

export interface SeniorCenterApplicationDetailResponse {
  statusCode: number;
  message: string;
  application: SeniorCenterApplicationDetailItem;
}

export interface SeniorCenterApplicationReviewRequest {
  status: 'approved' | 'rejected' | 'returned';
  comment?: string;
  eligibilityCategories?: string[];
}

export interface SeniorCenterApplicationReviewResponse {
  statusCode: number;
  message: string;
  applicationId: string;
  status: string;
  rfidNumber?: string | null;
}

export interface SeniorCenterTransportationReviewRequest {
  status: 'approved' | 'rejected';
  comment?: string;
  transportationOption?: string | null;
  /** Funding types: AAA Transportation / Metro Transportation (multi-select). */
  transportationFundingTypes?: string[] | null;
  /** Driving distance home → center in miles at approval time. */
  distanceToCenterMiles?: number | null;
}

export interface SeniorCenterTransportationReviewResponse {
  statusCode: number;
  message: string;
  applicationId: string;
  transportationRequestStatus: string;
}

export interface StaffEnrollmentSessionResponse {
  statusCode: number;
  message: string;
  userId: number;
  token: string;
}

export interface StaffRegistrationApplyResponse {
  statusCode: number;
  message: string;
  userId: number;
  token: string;
  applyResponse?: unknown;
}

export interface SeniorCenterApplicationFormResponse {
  statusCode: number;
  message: string;
  applicationId: string;
  userId?: number;
  status: string;
  formJson: Record<string, unknown>;
  previousApplicationId?: string | null;
  nextApplicationId?: string | null;
}

export interface SeniorCenterApplicationsListFilters {
  status?: string;
  approvalDateFrom?: string;
  approvalDateTo?: string;
  hasRfid?: boolean;
  /** Global search: name, phone, DOB, address, SSN last-4, app id, spouse/household, RFID. */
  searchText?: string;
  /** due (expiring+expired) | expiring | expired — skips financial year on the API. */
  expiryFilter?: 'due' | 'expiring' | 'expired';
}

@Injectable({
  providedIn: 'root',
})
export class SeniorCenterApplicationService {
  private readonly apiService = inject(ApiService);
  private readonly financialYearFilterService = inject(FinancialYearFilterService);

  getApplications(filters?: SeniorCenterApplicationsListFilters): Observable<SeniorCenterApplicationsListResponse> {
    return this.apiService.get<SeniorCenterApplicationsListResponse>(
      API_ENDPOINTS.SENIOR_CENTER.APPLICATIONS.LIST,
      this.buildListQueryParams(filters)
    );
  }

  getApplicationDetail(applicationId: string): Observable<SeniorCenterApplicationDetailResponse> {
    return this.apiService.get<SeniorCenterApplicationDetailResponse>(
      API_ENDPOINTS.SENIOR_CENTER.APPLICATIONS.DETAIL(applicationId),
      this.buildLocationQueryParams()
    );
  }

  getApplicationForm(applicationId: string): Observable<SeniorCenterApplicationFormResponse> {
    return this.apiService.get<SeniorCenterApplicationFormResponse>(
      API_ENDPOINTS.SENIOR_CENTER.APPLICATIONS.FORM(applicationId),
      this.buildLocationQueryParams()
    );
  }

  reviewApplication(
    applicationId: string,
    request: SeniorCenterApplicationReviewRequest
  ): Observable<SeniorCenterApplicationReviewResponse> {
    return this.apiService.put<SeniorCenterApplicationReviewResponse>(
      API_ENDPOINTS.SENIOR_CENTER.APPLICATIONS.REVIEW(applicationId),
      request,
      this.buildLocationQueryParams()
    );
  }

  /** Reopen rejected/returned application as draft; form_json is preserved. */
  reopenApplicationAsDraft(
    applicationId: string
  ): Observable<SeniorCenterApplicationReviewResponse> {
    return this.apiService.put<SeniorCenterApplicationReviewResponse>(
      API_ENDPOINTS.SENIOR_CENTER.APPLICATIONS.REOPEN(applicationId),
      {},
      this.buildLocationQueryParams()
    );
  }

  reviewTransportation(
    applicationId: string,
    request: SeniorCenterTransportationReviewRequest
  ): Observable<SeniorCenterTransportationReviewResponse> {
    return this.apiService.put<SeniorCenterTransportationReviewResponse>(
      API_ENDPOINTS.SENIOR_CENTER.APPLICATIONS.TRANSPORTATION_REVIEW(applicationId),
      request,
      this.buildLocationQueryParams()
    );
  }

  startEnrollmentSession(): Observable<StaffEnrollmentSessionResponse> {
    return this.apiService.post<StaffEnrollmentSessionResponse>(
      API_ENDPOINTS.SENIOR_CENTER.APPLICATIONS.ENROLLMENT_SESSION,
      {}
    );
  }

  applyRegistration(payload: Record<string, unknown>): Observable<StaffRegistrationApplyResponse> {
    return this.apiService.post<StaffRegistrationApplyResponse>(
      API_ENDPOINTS.SENIOR_CENTER.APPLICATIONS.REGISTRATION_APPLY,
      payload
    );
  }

  private buildLocationQueryParams(): HttpParams | undefined {
    const locationId = getSelectedLocation()?.id?.trim();
    if (!locationId) {
      return undefined;
    }

    return new HttpParams().set('locationId', locationId);
  }

  private buildListQueryParams(filters?: SeniorCenterApplicationsListFilters): HttpParams | undefined {
    const locationId = getSelectedLocation()?.id?.trim();
    const expiryFilter = filters?.expiryFilter?.trim().toLowerCase() as
      | 'due'
      | 'expiring'
      | 'expired'
      | undefined;
    const financialYear = this.financialYearFilterService.selectedYear;
    let params: HttpParams | undefined;

    if (locationId) {
      params = new HttpParams().set('locationId', locationId);
    }

    // Expiring views span years so staff see all renewals due.
    if (financialYear != null && !expiryFilter) {
      params = (params ?? new HttpParams()).set('financialYear', String(financialYear));
    }

    const status = filters?.status?.trim();
    if (status && status !== 'all' && !expiryFilter) {
      params = (params ?? new HttpParams()).set('status', status);
    }

    const approvalDateFrom = filters?.approvalDateFrom?.trim();
    if (approvalDateFrom) {
      params = (params ?? new HttpParams()).set('approvalDateFrom', approvalDateFrom);
    }

    const approvalDateTo = filters?.approvalDateTo?.trim();
    if (approvalDateTo) {
      params = (params ?? new HttpParams()).set('approvalDateTo', approvalDateTo);
    }

    if (filters?.hasRfid === true) {
      params = (params ?? new HttpParams()).set('hasRfid', 'true');
    }

    const searchText = filters?.searchText?.trim();
    if (searchText) {
      params = (params ?? new HttpParams()).set('searchText', searchText);
    }

    if (expiryFilter === 'due' || expiryFilter === 'expiring' || expiryFilter === 'expired') {
      params = (params ?? new HttpParams()).set('expiryFilter', expiryFilter);
    }

    return params;
  }
}
